class BeforeAfterSlider {
    constructor(element, sharedGrabberPosition) {
        this.element = element;
        this.imageContainer = this.element.querySelector('.image-container');
        this.sliderHandle = this.element.querySelector('.slider');
        this.afterImageWrapper = this.element.querySelector('.after-image-wrapper');
        this.sharedGrabberPosition = sharedGrabberPosition;
        this.sliderContent = this.element.querySelector('.slider-content');
        this.sliderWrapper = this.element.querySelector('.slider-wrapper');

        this.isDragging = false;

        this.init();
    }

    init() {
        if (this.sliderHandle && this.imageContainer && this.afterImageWrapper) {
            this.sliderHandle.addEventListener('mousedown', this.startDragging.bind(this));
            this.sliderHandle.addEventListener('touchstart', this.startDragging.bind(this));
            // Apply initial shared grabber position
            this.updateSliderPosition(this.sharedGrabberPosition.value);
        }
    }

    startDragging(e) {
        this.isDragging = true;
        document.addEventListener('mousemove', this.onDragging.bind(this));
        document.addEventListener('touchmove', this.onDragging.bind(this));
        document.addEventListener('mouseup', this.stopDragging.bind(this));
        document.addEventListener('touchend', this.stopDragging.bind(this));
        e.preventDefault(); // Prevent default drag behavior
    }

    onDragging(e) {
        if (!this.isDragging) return;

        const containerRect = this.imageContainer.getBoundingClientRect();
        let clientX = e.clientX;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
        }
        let offsetX = clientX - containerRect.left;

        if (offsetX < 0) offsetX = 0;
        if (offsetX > containerRect.width) offsetX = containerRect.width;

        const percentage = (offsetX / containerRect.width) * 100;
        this.updateSliderPosition(percentage);
        this.sharedGrabberPosition.value = percentage; // Update shared position
    }

    stopDragging() {
        this.isDragging = false;
        document.removeEventListener('mousemove', this.onDragging.bind(this));
        document.removeEventListener('touchmove', this.onDragging.bind(this));
        document.removeEventListener('mouseup', this.stopDragging.bind(this));
        document.removeEventListener('touchend', this.stopDragging.bind(this));
    }

    updateSliderPosition(percentage) {
        if (this.sliderHandle && this.afterImageWrapper) {
            this.sliderHandle.style.left = `${percentage}%`;
            this.afterImageWrapper.style.clipPath = `polygon(0 0, ${percentage}% 0, ${percentage}% 100%, 0 100%)`;
        }
    }

    activate() {
        this.element.classList.add('active');
        this.element.style.display = 'grid';
        this.updateSliderPosition(this.sharedGrabberPosition.value);
    }

    deactivate() {
        this.element.classList.remove('active');
        this.element.style.display = 'none';
    }
}

class GallerySection {
    constructor(element) {
        this.element = element;
        this.galleryContainer = this.element.querySelector('.gallery-container');
        this.sliders = Array.from(this.element.querySelectorAll('.before-after-slider'));
        this.galleryNavigation = this.element.querySelector('.gallery-navigation');

        this.currentIndex = 0;
        this.sharedGrabberPosition = { value: 50 }; // Object to hold shared position
        this.isInitialLoad = true;

        this.beforeAfterSliderInstances = [];
        this.sliders.forEach((sliderEl, index) => {
            this.beforeAfterSliderInstances.push(new BeforeAfterSlider(sliderEl, this.sharedGrabberPosition));
            const prevBtn = sliderEl.querySelector('.prev-btn');
            const nextBtn = sliderEl.querySelector('.next-btn');

            prevBtn.addEventListener('click', () => {
                this.showSlider((this.currentIndex > 0) ? this.currentIndex - 1 : this.sliders.length - 1);
            });

            nextBtn.addEventListener('click', () => {
                this.showSlider((this.currentIndex < this.sliders.length - 1) ? this.currentIndex + 1 : 0);
            });
        });

        this.init();
    }

    init() {
        this.setupNavigation();
        this.setupIntersectionObserver();
        this.showSlider(this.currentIndex, false); // Show initial slider without animation
    }

    setupNavigation() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => {
                this.showSlider((this.currentIndex > 0) ? this.currentIndex - 1 : this.sliders.length - 1);
            });
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => {
                this.showSlider((this.currentIndex < this.sliders.length - 1) ? this.currentIndex + 1 : 0);
            });
        }
    }

    setupIntersectionObserver() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.showSlider(this.currentIndex, true, 2000); // Trigger animation via showSlider
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.1 });

        observer.observe(this.element);
    }

    _triggerSliderAnimations(currentSliderInstance, targetElementSelector = null, staggerDelay = 200) {
        // First, remove 'animate' class from all elements in the section to reset animations
        const allAnimatedElements = this.element.querySelectorAll('.animate');
        allAnimatedElements.forEach(el => {
            el.classList.remove('animate');
        });

        // Hide navigation buttons at the start of the animation
        if (this.galleryNavigation) {
            this.galleryNavigation.classList.add('hide-buttons');
        }

        let elementsToAnimate = [];

        if (targetElementSelector) {
            elementsToAnimate = Array.from(currentSliderInstance.element.querySelectorAll(targetElementSelector));
        } else {
            const contentElements = Array.from(currentSliderInstance.element.querySelectorAll('.slider-content > *'));
            const sliderElements = [
                currentSliderInstance.imageContainer,
                currentSliderInstance.sliderHandle
            ];
            // Exclude nav buttons from this list as they are handled separately
            // const navButtons = Array.from(currentSliderInstance.element.querySelectorAll('.gallery-navigation .prev-btn, .gallery-navigation .next-btn'));

            // Define a polished animation order
            elementsToAnimate = [
                ...contentElements, // h2, p, button will stagger
                ...sliderElements,  // image container and slider handle
                // ...navButtons       // nav buttons
            ];
        }

        // We need to use a requestAnimationFrame to ensure the class removal has been processed by the browser
        // before adding it again.
        requestAnimationFrame(() => {
            let maxDelay = 0;
            elementsToAnimate.forEach((el, i) => {
                if (el) {
                    // Clear any existing timeouts for this element to avoid conflicts
                    if (el.animationTimeout) {
                        clearTimeout(el.animationTimeout);
                    }
                    const currentDelay = i * staggerDelay;
                    el.animationTimeout = setTimeout(() => {
                        el.classList.add('animate');
                    }, currentDelay);
                    maxDelay = Math.max(maxDelay, currentDelay);
                }
            });

            // Show navigation buttons after all animations are complete
            setTimeout(() => {
                if (this.galleryNavigation) {
                    this.galleryNavigation.classList.remove('hide-buttons');
                }
            }, maxDelay + 300); // Add a small buffer after the last animation
        });
    }

    showSlider(index, animate = true) {
        const oldIndex = this.currentIndex;
        const newIndex = index;

        this.beforeAfterSliderInstances.forEach((instance, i) => {
            if (i === newIndex) {
                instance.activate();
            } else {
                instance.deactivate();
            }
        });

        this.currentIndex = newIndex;

        this.updateBackgroundPosition();

        if (animate) {
            this.handleConditionalAnimation(oldIndex, newIndex);
        }
    }

    handleConditionalAnimation(oldIndex, newIndex) {
        const currentSliderInstance = this.beforeAfterSliderInstances[newIndex];
        if (!currentSliderInstance) return;

        if (this.isInitialLoad) {
            this._triggerSliderAnimations(currentSliderInstance);
            this.isInitialLoad = false;
            return;
        }

        const oldSliderContent = this.sliders[oldIndex].querySelector('.slider-content').innerHTML;
        const newSliderContent = this.sliders[newIndex].querySelector('.slider-content').innerHTML;

        if (oldSliderContent != newSliderContent) {
            this._triggerSliderAnimations(currentSliderInstance);
        } else {
            this._triggerSliderAnimations(currentSliderInstance, '.slider-wrapper, .slider');
        }
    }

    updateBackgroundPosition() {
        if (window.innerWidth <= 768) {
            // On mobile, update based on the current slide
            if (this.sliders.length > 1) {
                const percentage = (1 - (this.currentIndex / (this.sliders.length - 1))) * 100;
                this.element.style.backgroundPosition = `${percentage}% 0`;
            } else {
                this.element.style.backgroundPosition = 'center';
            }
        } else {
            // On desktop, update based on the current slide
            if (this.sliders.length > 1) {
                const percentage = (this.currentIndex / (this.sliders.length - 1)) * 100;
                this.element.style.backgroundPosition = `${percentage}% 0`;
            } else {
                this.element.style.backgroundPosition = 'center';
            }
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const gallerySections = document.querySelectorAll('.gallery-section');
    gallerySections.forEach(gallerySectionEl => {
        const gallery = new GallerySection(gallerySectionEl);
        window.addEventListener('resize', () => gallery.updateBackgroundPosition());
    });
});
